import { DbRef } from '@caresend/types';
import { objectMap, initWaypoint, initWaypointAction, getProcedureSamplePath, objectFilter, nullishFilter, decryptDraftWaypoints, diffObjectsDeep, getWaypointPath, getWaypointActionPath, getServiceRegionFromLocation } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { cleanInputWithInitializerDeep } from './helpers/helpers.js';
import { usePortalStore } from './index.js';
import { useDbSyncStore } from '../storeUtilities/dbSync/dbSyncStore.js';
import { setNestedChildOnRecord } from '../storeUtilities/recordManager/helper.js';
import 'firebase/auth';
import '../../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../ui/Icon.vue.js';
import '../../../../../functions/configuration/icons.js';
import '../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../functions/tracking/index.js';
import '../../../../../router/helpers/initRouter.js';
import { getStore } from '../../../../../store/helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { getServiceRegionIDsByShiftID } from '../../../../../functions/itinerary/tempHelpers.js';
import { initItinerary } from '../../../../../store/modules/itineraries/helpers.js';

const getItineraryRelationships = () => ({
    nurseID: true,
    supplyTransfers: true,
    shiftID: true,
    waypoints: {
        draftWaypoints: {
            supplyTransfers: {
                shipmentIDs: true,
            },
        },
        placeID: {
            placeGroupID: {
                placeGroupTypeID: true,
            },
        },
        waypointActions: {
            procedures: {
                bookingID: {
                    patientID: true,
                },
                orderID: {
                    bookings: {
                        patientID: true,
                    },
                    prescriberID: true,
                    schedulingUserID: true,
                },
                placeAccounts: true,
                dropOffPlaceGroups: true,
                supplyItems: true,
            },
            shipments: true,
        },
    },
});
const getShiftRelationships = () => ({
    userID: true,
    itineraries: {
        nurseID: true,
        waypoints: {
            waypointActions: {
                procedures: true,
            },
        },
    },
    staffingOffices: true,
    nursesOffered: true,
});
/**
 * Used to collect default data for ProcedureDetails.
 */
const getProcedureRelationships = () => ({
    orderID: {
        prescriberID: true,
    },
    bookingID: {
        patientID: true,
    },
    dropOffPlaceGroups: true,
    draftWaypointID: true,
});
const getOrderRelationships = () => {
    const waypointRelationships = {
        itineraryID: true,
        draftWaypoints: true,
        waypointActions: {
            procedures: {
                bookingID: {
                    patientID: true,
                },
            },
            waypointID: {
                itineraryID: true,
            },
        },
    };
    return {
        prescriberID: true,
        bookings: {
            patientID: true,
            procedures: {
                draftWaypointID: true,
                nurseID: true,
                waypointActions: {
                    waypointID: true,
                    procedures: true,
                },
            },
        },
        draftWaypoints: {
            itineraryID: {
                waypoints: waypointRelationships,
                nurseID: true,
            },
            waypointID: waypointRelationships,
        },
    };
};
const getWaypointRelationships = () => ({
    waypointActions: {
        procedures: {
            bookingID: {
                patientID: true,
            },
        },
        waypointID: {
            itineraryID: true,
        },
    },
});
const getFetchWithRelationshipsCallbacks = (itineraryID) => {
    const store = getStore();
    const portalStore = usePortalStore();
    const setServiceRegions = async (waypoints) => {
        const serviceRegions = {};
        const waypointPromises = Object.values(waypoints)
            .map(async (wp) => {
            const serviceRegion = await getServiceRegionFromLocation(wp.location, store.state.variables.variables, getValueOnce);
            if (!serviceRegion)
                return;
            serviceRegions[serviceRegion.id] = serviceRegion;
        });
        await Promise.all(waypointPromises);
        portalStore.serviceRegions.update(serviceRegions);
    };
    const bindResponseTimeouts = {};
    const waitForSubsequentBindResponses = (id, callback) => new Promise((resolve) => {
        const existingBind = bindResponseTimeouts[id];
        if (existingBind) {
            const { resolve: existingResolve, timeout } = existingBind;
            clearTimeout(timeout);
            existingResolve();
            existingBind.count += 1;
        }
        console.info(`Received ${id}. Awaiting subsequent bind responses...`);
        bindResponseTimeouts[id] = {
            count: 0,
            resolve,
            timeout: setTimeout(() => {
                callback();
                resolve();
            }, 200),
        };
    });
    const defaultCallbacks = store.getters['app/getDefaultFetchWithRelationshipsCallbacks'](store.commit, store.dispatch);
    const writeCallbacks = {
        ...defaultCallbacks,
        [DbRef.SHIFTS]: async (shifts, ...args) => {
            var _a;
            portalStore.shifts.update(shifts);
            (_a = defaultCallbacks[DbRef.SHIFTS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, shifts, ...args);
            Object.values(shifts).forEach(async (shift) => {
                const serviceRegionIDs = await getServiceRegionIDsByShiftID(shift.id);
                const serviceRegions = Object.fromEntries(serviceRegionIDs.map((id) => {
                    const sr = portalStore.serviceRegions.getRaw(id);
                    return sr ? [id, sr] : [];
                }));
                portalStore.serviceRegions.update(serviceRegions);
            });
        },
        [DbRef.BOOKINGS]: async (bookings, ...args) => {
            var _a;
            portalStore.bookings.update(bookings);
            (_a = defaultCallbacks[DbRef.BOOKINGS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, bookings, ...args);
        },
        [DbRef.OFFICES]: async (offices, ...args) => {
            var _a;
            portalStore[DbRef.OFFICES].update(offices);
            (_a = defaultCallbacks[DbRef.OFFICES]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, offices, ...args);
        },
        [DbRef.ORDERS]: async (orders, ...args) => {
            var _a;
            portalStore[DbRef.ORDERS].update(orders);
            (_a = defaultCallbacks[DbRef.ORDERS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, orders, ...args);
        },
        [DbRef.PLACES]: async (places, ...args) => {
            var _a;
            portalStore[DbRef.PLACES].update(places);
            (_a = defaultCallbacks[DbRef.PLACES]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, places, ...args);
        },
        [DbRef.PLACE_ACCOUNTS]: async (placeAccounts, ...args) => {
            var _a;
            portalStore[DbRef.PLACE_ACCOUNTS].update(placeAccounts);
            (_a = defaultCallbacks[DbRef.PLACE_ACCOUNTS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, placeAccounts, ...args);
        },
        [DbRef.PLACE_GROUPS]: async (placeGroups, ...args) => {
            var _a;
            portalStore[DbRef.PLACE_GROUPS].update(placeGroups);
            (_a = defaultCallbacks[DbRef.PLACE_GROUPS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, placeGroups, ...args);
        },
        [DbRef.PLACE_GROUP_TYPES]: async (placeGroupTypes, ...args) => {
            var _a;
            portalStore[DbRef.PLACE_GROUP_TYPES].update(placeGroupTypes);
            (_a = defaultCallbacks[DbRef.PLACE_GROUP_TYPES]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, placeGroupTypes, ...args);
        },
        [DbRef.USERS]: async (users, ...args) => {
            var _a;
            portalStore.users.update(users);
            (_a = defaultCallbacks[DbRef.USERS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, users, ...args);
        },
        /** Extensions for write callbacks */
        [DbRef.WAYPOINTS]: async (waypoints, acc, isBindResponse, ...args) => {
            const set = (fixed = waypoints) => {
                var _a;
                portalStore.waypoints.update(fixed);
                (_a = defaultCallbacks[DbRef.WAYPOINTS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, fixed, acc, isBindResponse, ...args);
                setServiceRegions(fixed);
            };
            const cleanWaypoints = objectMap(cloneDeep(waypoints), (wp) => cleanInputWithInitializerDeep(wp, initWaypoint(wp.type)));
            if (isBindResponse) {
                return waitForSubsequentBindResponses('itinerary/waypoints', () => {
                    let changed = false;
                    let fixed;
                    Object.values(cleanWaypoints).forEach((newWp) => {
                        const prevWp = portalStore.waypoints.getRaw(newWp.id);
                        if (prevWp) {
                            const diffs = diffObjectsDeep(prevWp, newWp);
                            const diffsToFix = useDbSyncStore().validateIncomingDiffs(DbRef.ITINERARIES, getWaypointPath(newWp.id), diffs, ['transitInfo']);
                            const diffCount = Object.values(diffs).length - Object.values(diffsToFix).length;
                            if (diffCount)
                                console.info('Waypoint diffs:', JSON.stringify(diffs, null, 2));
                            Object.entries(diffsToFix).forEach(([subpath, value]) => {
                                fixed = setNestedChildOnRecord(waypoints, `${newWp.id}/${subpath}`, value);
                            });
                            if (!diffCount)
                                return;
                        }
                        changed = true;
                    });
                    if (!changed)
                        return;
                    console.info('Writing waypoints to store');
                    set(fixed);
                });
            }
            set();
        },
        [DbRef.WAYPOINT_ACTIONS]: async (waypointActions, acc, isBindResponse, ...args) => {
            const set = (fixed = waypointActions) => {
                var _a;
                portalStore.waypointActions.update(fixed);
                (_a = defaultCallbacks[DbRef.WAYPOINT_ACTIONS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, fixed, acc, isBindResponse, ...args);
            };
            const cleanWaypointActions = objectMap(cloneDeep(waypointActions), (wpa) => { var _a; return cleanInputWithInitializerDeep(wpa, initWaypointAction(wpa.type, (_a = wpa.waypointID) !== null && _a !== void 0 ? _a : '')); });
            if (isBindResponse) {
                return waitForSubsequentBindResponses('itinerary/waypointActions', () => {
                    let changed = false;
                    let fixed;
                    Object.values(cleanWaypointActions).forEach((newWpa) => {
                        const prevWpa = portalStore.itinerary.getters.currentWaypointActions()[newWpa.id];
                        if (prevWpa) {
                            const diffs = diffObjectsDeep(prevWpa, newWpa);
                            const diffsToFix = useDbSyncStore().validateIncomingDiffs(DbRef.ITINERARIES, getWaypointActionPath(newWpa.id), diffs);
                            Object.entries(diffsToFix).forEach(([subpath, value]) => {
                                fixed = setNestedChildOnRecord(waypointActions, `${newWpa.id}/${subpath}`, value);
                            });
                            const diffCount = Object.values(diffs).length - Object.values(diffsToFix).length;
                            if (!diffCount)
                                return;
                            console.info('Waypoint action diffs:', JSON.stringify(diffs, null, 2));
                        }
                        changed = true;
                    });
                    if (!changed)
                        return;
                    console.info('Writing waypoint actions to store');
                    set(fixed);
                });
            }
            set();
        },
        [DbRef.PROCEDURES]: async (procedures, ...args) => {
            var _a;
            portalStore.procedures.update(procedures);
            (_a = defaultCallbacks[DbRef.PROCEDURES]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, procedures, ...args);
            /** Register samples as records in the record manager */
            Object.values(procedures).forEach((p) => {
                var _a;
                Object.values((_a = p.samples) !== null && _a !== void 0 ? _a : {}).forEach((sample) => {
                    const path = getProcedureSamplePath(p.id, sample.id);
                    portalStore.samples.set(sample, false, { path });
                });
            });
            /** Ad-hoc logic to ensure we fetch procedure contacts */
            const contacts = objectFilter(objectMap(procedures, (p) => { var _a; return (_a = p.contact) === null || _a === void 0 ? void 0 : _a.userID; }), nullishFilter);
            const userPromises = Object.keys(contacts).map((userID) => getValueOnce(`${DbRef.USERS}/${userID}`));
            const userContacts = (await Promise.all(userPromises)).filter(nullishFilter);
            store.commit('users/SET_USERS', userContacts);
        },
        [DbRef.DRAFT_WAYPOINTS]: async (draftWaypoints, acc, isBindResponse, ...args) => {
            var _a;
            try {
                const decryptedDraftWaypoints = await decryptDraftWaypoints(draftWaypoints);
                portalStore.draftWaypoints.update(decryptedDraftWaypoints);
                (_a = defaultCallbacks[DbRef.DRAFT_WAYPOINTS]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, decryptedDraftWaypoints, acc, isBindResponse, ...args);
            }
            catch (error) {
                console.error('Error decrypting draft waypoints:', error);
            }
        },
        [DbRef.ITINERARIES]: async (itineraries, acc, isBindResponse, ...args) => {
            var _a;
            portalStore.itineraries.update(itineraries);
            const set = (itinerary) => {
                var _a;
                if (itinerary)
                    portalStore.itinerary.set.value(itinerary);
                portalStore.itineraries.update(itineraries);
                (_a = defaultCallbacks[DbRef.ITINERARIES]) === null || _a === void 0 ? void 0 : _a.call(defaultCallbacks, itineraries, acc, isBindResponse, ...args);
            };
            if (!itineraryID)
                return set();
            const initial = initItinerary();
            const itinerary = cleanInputWithInitializerDeep((_a = itineraries[itineraryID]) !== null && _a !== void 0 ? _a : {}, initial);
            if (!itineraries[itineraryID])
                return;
            if (isBindResponse) {
                return waitForSubsequentBindResponses(itineraryID, () => {
                    const prevItinerary = cloneDeep(portalStore.itinerary.value);
                    const valsEqual = isEqual(itinerary, prevItinerary);
                    if (valsEqual) {
                        console.info('Received itinerary. Equal values, skipping store write');
                        return;
                    }
                    console.info('Writing itinerary to store');
                    set(itinerary);
                });
            }
            set(itinerary);
        },
    };
    return writeCallbacks;
};

export { getFetchWithRelationshipsCallbacks, getItineraryRelationships, getOrderRelationships, getProcedureRelationships, getShiftRelationships, getWaypointRelationships };
